/**
 * MacroManager - Executes campaign macros (onLaunch, onJoin, onExit, onInactive)
 *
 * Responsibilities:
 * - Execute onLaunchMacro when game launches (awaited before matchmakeable)
 * - Execute onJoinMacro when match found (awaited before HOST_READY / stream start)
 * - Execute onExitMacro when all players leave with no restart (awaited before pool re-entry)
 * - Execute onInactiveMacro on a 30s interval while no players are connected
 * - Delegate step playback to MacroStepRunner
 */

import type { PavilionCoordinator } from '../../pavilion/PavilionCoordinator';
import type { IHostAnalyticsManager } from '../../../services/analytics/IHostAnalyticsManager';
import { MacroStepRunner, type MacroGuestFrame } from './MacroStepRunner';
import { DEFAULT_INACTIVE_MACRO_INTERVAL_MS } from '@playcastdotio/Messaging';

export type { MacroGuestFrame };

export class MacroManager {
  private static readonly MAX_JOIN_MACRO_WAIT_MS = 4_000;

  private pavilionCoordinator: PavilionCoordinator;
  private analytics: IHostAnalyticsManager;
  private stepRunner = new MacroStepRunner();
  private hasExecutedLaunchMacro = false;
  private launchMacroGeneration = 0;
  private inactiveIntervalId: ReturnType<typeof setInterval> | null = null;

  constructor(pavilionCoordinator: PavilionCoordinator, analytics: IHostAnalyticsManager) {
    this.pavilionCoordinator = pavilionCoordinator;
    this.analytics = analytics;
  }

  public async executeOnLaunchMacro(): Promise<void> {
    if (this.hasExecutedLaunchMacro) {
      console.log('[MacroManager] onLaunchMacro already executed, skipping');
      return;
    }

    const config = this.pavilionCoordinator.getCampaignConfig();
    if (!config) {
      console.log('[MacroManager] No campaign config available, skipping onLaunchMacro');
      return;
    }

    const { onLaunchMacro } = config;
    if (!onLaunchMacro || onLaunchMacro.length === 0) {
      console.log('[MacroManager] No onLaunchMacro defined for campaign:', config.campaignId);
      return;
    }

    console.log(`[MacroManager] Executing onLaunchMacro for "${config.displayName}":`, onLaunchMacro);

    const generationAtStart = this.launchMacroGeneration;
    let success = false;
    try {
      const result = await this.runMacroSteps(onLaunchMacro, 'Launch');
      if (!result.success) {
        console.error('[MacroManager] onLaunchMacro failed:', result.reason);
        return;
      }
      if (generationAtStart !== this.launchMacroGeneration) {
        console.log('[MacroManager] onLaunchMacro finished after game closed — not marking as executed');
        return;
      }
      this.hasExecutedLaunchMacro = true;
      success = true;
      console.log('[MacroManager] onLaunchMacro completed successfully');
    } catch (error) {
      console.error('[MacroManager] onLaunchMacro failed:', error);
    } finally {
      this.analytics.sendMacroExecutedEvent({
        macroType: 'onLaunch',
        success,
        campaignId: config.campaignId,
      });
    }
  }

  public async executeOnJoinMacro(): Promise<void> {
    this.stopInactiveLoop();

    const config = this.pavilionCoordinator.getCampaignConfig();
    if (!config) {
      console.log('[MacroManager] No campaign config available, skipping onJoinMacro');
      return;
    }

    const { onJoinMacro } = config;
    if (!onJoinMacro || onJoinMacro.length === 0) {
      console.log('[MacroManager] No onJoinMacro defined for campaign:', config.campaignId);
      return;
    }

    console.log(`[MacroManager] Executing onJoinMacro for "${config.displayName}":`, onJoinMacro);

    let success = false;
    const macroPromise = this.runMacroSteps(onJoinMacro, 'Player join')
      .then((result) => {
        if (!result.success) {
          console.error('[MacroManager] onJoinMacro failed:', result.reason);
          return;
        }
        success = true;
        console.log('[MacroManager] onJoinMacro completed successfully');
      })
      .catch((error) => console.error('[MacroManager] onJoinMacro failed:', error))
      .finally(() => {
        this.analytics.sendMacroExecutedEvent({
          macroType: 'onJoin',
          success,
          campaignId: config.campaignId,
        });
      });

    const timeoutPromise = new Promise<void>((resolve) => {
      setTimeout(() => {
        console.warn(`[MacroManager] Player join macro still running after ${MacroManager.MAX_JOIN_MACRO_WAIT_MS}ms, proceeding without waiting.`);
        resolve();
      }, MacroManager.MAX_JOIN_MACRO_WAIT_MS);
    });

    await Promise.race([macroPromise, timeoutPromise]);
  }

  public async executeOnExitMacro(): Promise<void> {
    const config = this.pavilionCoordinator.getCampaignConfig();
    if (!config) {
      console.log('[MacroManager] No campaign config available, skipping onExitMacro');
      this.startInactiveLoop();
      return;
    }

    const { onExitMacro } = config;
    if (!onExitMacro || onExitMacro.length === 0) {
      console.log('[MacroManager] No onExitMacro defined for campaign:', config.campaignId);
      this.startInactiveLoop();
      return;
    }

    console.log(`[MacroManager] Executing onExitMacro for "${config.displayName}":`, onExitMacro);

    let success = false;
    try {
      const result = await this.runMacroSteps(onExitMacro, 'Player Exit');
      if (result.success) {
        success = true;
        console.log('[MacroManager] onExitMacro completed successfully');
      } else {
        console.error('[MacroManager] onExitMacro failed:', result.reason);
      }
    } catch (error) {
      console.error('[MacroManager] onExitMacro failed:', error);
    } finally {
      this.analytics.sendMacroExecutedEvent({
        macroType: 'onExit',
        success,
        campaignId: config.campaignId,
      });
    }

    this.startInactiveLoop();
  }

  public async executeOnInactiveMacro(): Promise<void> {
    const config = this.pavilionCoordinator.getCampaignConfig();
    if (!config) return;

    const { onInactiveMacro } = config;
    if (!onInactiveMacro || onInactiveMacro.length === 0) return;

    let success = false;
    try {
      const result = await this.runMacroSteps(onInactiveMacro, 'Inactive');
      success = result.success;
    } catch (error) {
      console.error('[MacroManager] onInactiveMacro failed:', error);
    } finally {
      this.analytics.sendMacroExecutedEvent({
        macroType: 'onInactive',
        success,
        campaignId: config.campaignId,
      });
    }
  }

  public stopInactiveLoop(): void {
    if (this.inactiveIntervalId) {
      clearInterval(this.inactiveIntervalId);
      this.inactiveIntervalId = null;
      console.log('[MacroManager] Stopped inactive loop');
    }
  }

  public reset(): void {
    this.launchMacroGeneration++;
    this.hasExecutedLaunchMacro = false;
    this.stopInactiveLoop();
    console.log('[MacroManager] Reset launch macro tracking');
  }

  /**
   * Run an array of macro step strings sequentially.
   * Throws when step playback fails unexpectedly; returns `{ success: false }`
   * when a recorded click cannot be placed.
   */
  public async runMacroSteps(
    steps: string[],
    label = 'Macro',
    frame?: MacroGuestFrame,
  ): Promise<{ success: boolean; reason?: string }> {
    const result = await this.stepRunner.runSteps(steps, label, frame);
    if (!result.success) {
      throw new Error(result.reason ?? 'Macro step playback failed');
    }
    return result;
  }

  private startInactiveLoop(): void {
    this.stopInactiveLoop();

    // Interval comes from the campaign config (source of truth), defaulting
    // to the legacy 30s when not configured.
    const intervalMs = this.pavilionCoordinator.getCampaignConfig()?.inactiveMacroIntervalMs
      ?? DEFAULT_INACTIVE_MACRO_INTERVAL_MS;

    this.inactiveIntervalId = setInterval(() => {
      void this.executeOnInactiveMacro();
    }, intervalMs);

    console.log(`[MacroManager] Started inactive loop (every ${intervalMs}ms)`);
  }
}
